/**
 * Presentation model for the Voyage Radar "Vessel Tracks" panel section.
 *
 * A track record is written by the API as `trackId`, `name`, `mmsi`,
 * `vesselName`, `shipType`, `from`, `to`, `count`, `spanLabel`, `spanClamped`,
 * `truncated` and `s3Key` — see the `kind === "track"` branch of
 * `saveSensorArtifact` in eetaskapi/utils.js. Only `trackId`, `name` and `mmsi`
 * can be relied on; everything else is optional, either because it was absent
 * upstream or because the record predates the field.
 *
 * NOTE `name` is the FETCH TIME, not the vessel's name — the field is shared
 * with `refreshHistory`, where it means the same thing. The vessel's name is
 * `vesselName`. Confusing the two is the easiest mistake to make here.
 *
 * The shape differs from `refreshHistory` in the one way that decides this whole
 * section: a refresh is ONE thing at ONE time, so a day-grouped list fits it. A
 * track is a WINDOW bought for a SHIP, so the axis people search along is the
 * hull. Hence grouping by MMSI here where the snapshot model groups by UTC day.
 *
 * Every label that also appears on a snapshot row — ages, dates, times,
 * durations — is imported from the snapshot model rather than restated, so the
 * two lists cannot drift apart.
 */

import {
  formatVoyageAgo,
  formatVoyageTime,
} from '../refresh-history/voyage-refresh-history.model';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Vessel groups the section renders before the "show all" toggle. Far smaller
 * than the snapshot cap because a group can expand into its own track rows —
 * three collapsed groups is a section, ten is a scroll.
 */
export const VOYAGE_TRACKS_RECENT = 3;

/**
 * Names that are present in the data but are not a vessel's name.
 *
 * AIS pads the 20-character name field with '@' and feeds differ on whether
 * they strip it, so the padding arrives as a legitimate-looking string. The
 * rest are the placeholders operators substitute when the field is blank.
 * Compared upper-cased, after `cleanVesselName` has trimmed and de-padded.
 */
const UNNAMED = new Set([
  '', '-', '--', '0', 'N/A', 'NA', 'NIL', 'NONE', 'NO NAME', 'NONAME',
  'NULL', 'UNAVAILABLE', 'UNDEFINED', 'UNKNOWN', 'UNKNOWN VESSEL', 'VESSEL',
]);

/** Ship types that carry no more information than showing nothing would. */
const UNTYPED = new Set(['', 'OTHER', 'UNKNOWN', 'UNSPECIFIED', 'N/A', 'NA', 'NULL', 'UNDEFINED']);

/**
 * Fallback dot colours for a vessel that is not on the map right now, so its
 * colour cannot be read from the live feed. Picked by MMSI so a given hull keeps
 * the same colour across sessions rather than shuffling on every render.
 *
 * NO BLUES, AND THAT IS THE RULE. This list also colours the track a vessel
 * draws, and a track is drawn on water: every basemap paints the sea light blue
 * and its shipping lanes, depth contours and maritime boundaries in blues and
 * greys on top of it. Two of the seven entries here used to be `#38BDF8` and
 * `#60A5FA`, so roughly a third of all hulls drew a path indistinguishable from
 * basemap furniture.
 *
 * What is left is warm and saturated — rose, violet, emerald, amber, pink, lime,
 * orange — none of which any basemap uses for water features. Changing the list
 * reshuffles which hull gets which colour once; that is a one-off and worth it.
 */
const FALLBACK_COLORS = [
  '#F43F5E', '#A855F7', '#10B981', '#F59E0B', '#EC4899', '#84CC16', '#F97316',
];

/**
 * The track colour when a vessel has no colour of its own — no live feed entry
 * and no identity to hash. Rose, for the reason above: it cannot be mistaken
 * for a sea lane, and it is not the amber the located-vessel markers use.
 */
export const VOYAGE_DEFAULT_TRACK_COLOR = '#F43F5E';

/** Trailing AIS '@' padding, collapsed whitespace, trimmed. */
function cleanVesselName(raw: any): string {
  return String(raw ?? '').replace(/@+$/g, '').replace(/\s+/g, ' ').trim();
}

/** How a vessel is labelled wherever it appears — panel, ON MAP bar, filename. */
export interface VoyageVesselIdentity {
  mmsi: string;
  /** Primary line: the vessel's name, or "MMSI 219018123". */
  label: string;
  /** Secondary line: the MMSI when named, else the ship type or a note. */
  sublabel: string;
  /** False when the feed carried no usable name — drives the muted styling. */
  isNamed: boolean;
  /** Ship type when it says something, else null. */
  shipType: string | null;
}

/**
 * The one place a vessel's display identity is decided.
 *
 * MMSI is the identity and is always present; the name is only a label. So the
 * fallback is never the word "Unknown" — it is the MMSI, promoted to the primary
 * line and prefixed, because a bare number above the same bare number reads as a
 * rendering bug rather than as missing data.
 *
 * `liveName` wins when supplied: a name the vessel is broadcasting right now is
 * fresher than one stored with a track a week ago.
 *
 * Note the `name === mmsi` rejection. `VoyageMapController.activateVessel` builds
 * its vessel with `String(p['Name'] ?? mmsi)`, and that value is what gets sent
 * as `vesselName` — so our own fallback comes back to us through storage and has
 * to be recognised, or the row renders "219018123 · 219018123".
 */
export function resolveVesselIdentity(
  record: any,
  liveName?: string | null
): VoyageVesselIdentity {
  const mmsi = String(record?.mmsi ?? record?.MMSI ?? '').trim();

  const shipTypeRaw = cleanVesselName(record?.shipType ?? record?.['Ship Type']);
  const shipType = UNTYPED.has(shipTypeRaw.toUpperCase()) ? null : shipTypeRaw;

  // Deliberately NOT `record.name` — on a track record that is the fetch time,
  // and a date string passes every junk check below as a perfectly good "name".
  const candidates = [liveName, record?.vesselName, record?.['Name']];
  for (const candidate of candidates) {
    const name = cleanVesselName(candidate);
    if (!name || UNNAMED.has(name.toUpperCase())) continue;
    // Our own MMSI fallback, round-tripped through storage.
    if (mmsi && name === mmsi) continue;
    return { mmsi, label: name, sublabel: mmsi, isNamed: true, shipType };
  }

  return {
    mmsi,
    label: mmsi ? `MMSI ${mmsi}` : 'Unknown vessel',
    sublabel: shipType || 'Unnamed vessel',
    isNamed: false,
    shipType,
  };
}

// The fetch-balance resolver that lived here moved to ../voyage-credits.model.ts
// (`voyageCreditsRemaining`) when the refresh/fetch counter pair collapsed into
// one API-credit wallet — tracks and refreshes now spend from the same balance.

/** Stable per-MMSI colour for a vessel with no live feed entry to read one from. */
export function fallbackVesselColor(mmsi: string): string {
  let hash = 0;
  for (let i = 0; i < mmsi.length; i++) hash = (hash * 31 + mmsi.charCodeAt(i)) >>> 0;
  return FALLBACK_COLORS[hash % FALLBACK_COLORS.length];
}

/** One purchased track, ready to render. */
export interface VoyageTrackRow {
  /** The raw trackHistory record — handed straight back to replay/download. */
  data: any;
  trackId: string;
  mmsi: string;
  /** When the track was bought. */
  fetchedMs: number;
  /** "3d ago". */
  ago: string;
  /** Covered window, or null on a record that stored no from/to. */
  fromMs: number | null;
  toMs: number | null;
  /** "24 h" — the operator span label, or a duration derived from the window. */
  spanLabel: string;
  /** "12 Jul 08:00 → 13 Jul 08:00", or '' with no window. */
  window: string;
  /** Upstream position count. May exceed what was stored — see `truncated`. */
  points: number | null;
  /** The request hit the operator's 90-day ceiling. */
  clamped: boolean;
  /** Upstream reported more positions than it returned. */
  truncated: boolean;
}

/** Every track bought for one vessel. */
export interface VoyageTrackGroup {
  mmsi: string;
  identity: VoyageVesselIdentity;
  /** Live feed colour when the vessel is on the map, else a stable fallback. */
  color: string;
  /** True when this MMSI is in the snapshot currently drawn. */
  onMap: boolean;
  rows: VoyageTrackRow[];
  /** Newest fetch in the group — the sort key. */
  newestMs: number;
}

export interface VoyageTrackOptions {
  /** Reference time for the relative labels. */
  now: number;
  /** Vessel groups to keep, newest-fetch first. Null/undefined keeps all. */
  limit?: number | null;
  /**
   * MMSI → live name/colour for every vessel in the snapshot on the map.
   *
   * Presence, colour and the preferred name all come from this one map:
   * membership IS "in view", so a separate MMSI set could only ever disagree
   * with it.
   */
  liveVessels?: Map<string, { name?: string | null; color?: string | null }> | null;
}

/** Extent of a sensor's purchased tracks. Null when there is nothing usable. */
export interface VoyageTrackSummary {
  tracks: number;
  vessels: number;
}

/** One usable track record with its timestamps already parsed. */
interface VoyageTrackEntry {
  data: any;
  trackId: string;
  mmsi: string;
  fetchedMs: number;
  fromMs: number | null;
  toMs: number | null;
}

function parseMs(raw: any): number | null {
  if (raw == null) return null;
  const ms = new Date(raw).getTime();
  return isNaN(ms) ? null : ms;
}

/**
 * Newest-fetch-first, with unparseable and id-less records dropped rather than
 * rendered as "Invalid Date". Everything else here starts from this.
 */
function normalizeVoyageTracks(history: any[]): VoyageTrackEntry[] {
  return (Array.isArray(history) ? history : [])
    .map((data) => ({
      data,
      trackId: data?.trackId,
      mmsi: String(data?.mmsi ?? ''),
      fetchedMs: parseMs(data?.name) ?? NaN,
      fromMs: parseMs(data?.from),
      toMs: parseMs(data?.to),
    }))
    .filter((e) => !!e.trackId && !!e.mmsi && !isNaN(e.fetchedMs))
    .sort((a, b) => b.fetchedMs - a.fetchedMs);
}

/** "12 Jul", with the year only when it is not the current one. */
function shortDate(timeMs: number, now: number): string {
  const d = new Date(timeMs);
  const year = d.getUTCFullYear();
  const suffix = year === new Date(now).getUTCFullYear() ? '' : ` ${year}`;
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}${suffix}`;
}

/**
 * "12 Jul 08:00 → 13 Jul 08:00", collapsing to "13 Jul 02:00 → 08:00" when both
 * ends fall on the same UTC day. The sidebar is 320px — the repeated date is the
 * first thing that can go without losing meaning.
 */
function formatTrackWindow(fromMs: number | null, toMs: number | null, now: number): string {
  if (fromMs == null || toMs == null) return '';
  const from = new Date(fromMs);
  const to = new Date(toMs);
  const sameDay =
    from.getUTCFullYear() === to.getUTCFullYear() &&
    from.getUTCMonth() === to.getUTCMonth() &&
    from.getUTCDate() === to.getUTCDate();

  const left = `${shortDate(fromMs, now)} ${formatVoyageTime(fromMs)}`;
  const right = sameDay
    ? formatVoyageTime(toMs)
    : `${shortDate(toMs, now)} ${formatVoyageTime(toMs)}`;
  return `${left} → ${right}`;
}

/** "24 h" / "7 d" derived from the window, for a record with no `spanLabel`. */
function deriveSpanLabel(fromMs: number | null, toMs: number | null): string {
  if (fromMs == null || toMs == null) return 'track';
  const hours = Math.max(1, Math.round((toMs - fromMs) / 3600000));
  return hours < 48 ? `${hours} h` : `${Math.round(hours / 24)} d`;
}

/** The three facts that describe a stored track's extent. */
export interface VoyageTrackDescription {
  /** "24 h" — the operator's own label, or one derived from the window. */
  spanLabel: string;
  /** "12 Jul 08:00 → 13 Jul 08:00", or '' on a record that stored no window. */
  window: string;
  /** The operator's reported position count, or null when it stored none. */
  points: number | null;
}

/**
 * What a single track record covers, formatted.
 *
 * Pulled out of the row builder so a track can be described OUTSIDE the list —
 * the pending ON MAP row names the span and point count of a track that is
 * still loading, and it must read identically to the row that was clicked to
 * start it. Two formatters would drift the moment one of them was tuned.
 */
export function describeVoyageTrack(record: any, now: number): VoyageTrackDescription {
  const fromMs = parseMs(record?.from);
  const toMs = parseMs(record?.to);
  return {
    spanLabel: cleanVesselName(record?.spanLabel) || deriveSpanLabel(fromMs, toMs),
    window: formatTrackWindow(fromMs, toMs, now),
    // The operator's own count, which is what `truncated` is measured against —
    // it can exceed the number of positions actually stored.
    points: typeof record?.count === 'number' ? record.count : null,
  };
}

/**
 * Vessel groups, newest purchase first, optionally capped.
 *
 * The identity is resolved per GROUP rather than per record: one MMSI can have a
 * track bought while it was broadcasting a name and another bought while it was
 * not, and taking the newest record's name would flip the group's title to
 * "MMSI 219…" the moment a nameless fetch lands.
 */
export function buildVoyageTrackGroups(
  history: any[],
  opts: VoyageTrackOptions
): VoyageTrackGroup[] {
  const entries = normalizeVoyageTracks(history);

  const byMmsi = new Map<string, VoyageTrackEntry[]>();
  for (const entry of entries) {
    const list = byMmsi.get(entry.mmsi);
    if (list) list.push(entry);
    else byMmsi.set(entry.mmsi, [entry]);
  }

  const groups: VoyageTrackGroup[] = [];

  byMmsi.forEach((list, mmsi) => {
    const live = opts.liveVessels?.get(mmsi) ?? null;
    const liveName = cleanVesselName(live?.name) || null;

    // Best name wins across the group, live feed first.
    let identity = resolveVesselIdentity(list[0].data, liveName);
    if (!identity.isNamed) {
      for (const entry of list) {
        const candidate = resolveVesselIdentity(entry.data, liveName);
        if (candidate.isNamed) {
          identity = candidate;
          break;
        }
      }
    }

    const rows: VoyageTrackRow[] = list.map((entry) => ({
      data: entry.data,
      trackId: entry.trackId,
      mmsi,
      fetchedMs: entry.fetchedMs,
      ago: formatVoyageAgo(entry.fetchedMs, opts.now),
      fromMs: entry.fromMs,
      toMs: entry.toMs,
      ...describeVoyageTrack(entry.data, opts.now),
      clamped: !!entry.data?.spanClamped,
      truncated: !!entry.data?.truncated,
    }));

    groups.push({
      mmsi,
      identity,
      color: live?.color || fallbackVesselColor(mmsi),
      onMap: !!live,
      rows,
      newestMs: list[0].fetchedMs,
    });
  });

  groups.sort((a, b) => b.newestMs - a.newestMs);
  return opts.limit != null && opts.limit >= 0 ? groups.slice(0, opts.limit) : groups;
}

/** Totals for the section header and the "show all" toggle. */
export function voyageTrackSummary(history: any[]): VoyageTrackSummary {
  const entries = normalizeVoyageTracks(history);
  return {
    tracks: entries.length,
    vessels: new Set(entries.map((e) => e.mmsi)).size,
  };
}

/** The record for a trackId, or null. Used to narrate the track on the map. */
export function findVoyageTrack(history: any[], trackId: string | null): any | null {
  if (!trackId) return null;
  return normalizeVoyageTracks(history).find((e) => e.trackId === trackId)?.data ?? null;
}

/**
 * "MAERSK_DENVER_24h_20260713T0800.json".
 *
 * A feed name can carry slashes, spaces and unicode, so it is reduced to
 * filename-safe characters and capped. An unnamed vessel yields
 * "MMSI636092371_…" rather than the "null_…" a naive template would produce.
 */
export function voyageTrackFileName(record: any, identity: VoyageVesselIdentity): string {
  const stem = (identity.isNamed ? identity.label : `MMSI${identity.mmsi}`)
    .replace(/[^A-Za-z0-9._-]+/g, '_')
    .replace(/_{2,}/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, 48);

  const span = String(record?.spanLabel ?? '')
    .replace(/[^A-Za-z0-9]+/g, '')
    .slice(0, 8);

  const endMs = parseMs(record?.to) ?? parseMs(record?.name);
  const stamp = endMs != null
    ? new Date(endMs).toISOString().replace(/[-:]/g, '').replace(/\.\d+Z$/, 'Z')
    : '';

  return [stem || 'track', span, stamp].filter(Boolean).join('_') + '.json';
}
